import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Filter } from "lucide-react";
import { Section, SectionTitle, StatusBadge, TeamLogo, Reveal } from "../components/UIBits";
import { TOURNAMENT, phaseLabel } from "../lib/constants";
import { useT, formatDayLocalized } from "../lib/i18n";
import api from "../lib/api";

const TeamRow = ({ team, placeholder, score }) => {
    const inner = (
        <>
            <TeamLogo team={team} size={32} />
            <span className="flex-1 min-w-0 truncate text-sm font-semibold">{team?.name || placeholder}</span>
            <span className="font-display text-2xl shrink-0">{score}</span>
        </>
    );
    if (team?.id) {
        return (
            <Link to={`/equipes/${team.id}`} className="flex items-center gap-3 hover:bg-white/5 -mx-2 px-2 py-1 transition" data-testid={`schedule-team-link-${team.id}`}>
                {inner}
            </Link>
        );
    }
    return <div className="flex items-center gap-3 px-2 py-1">{inner}</div>;
};

const TeamCell = ({ team, placeholder }) => {
    if (team?.id) {
        return (
            <Link to={`/equipes/${team.id}`} className="flex items-center gap-2 hover:text-[#7FD93C] transition" data-testid={`schedule-team-cell-${team.id}`}>
                <TeamLogo team={team} size={28} />
                <span className="truncate">{team.name}</span>
            </Link>
        );
    }
    return <div className="flex items-center gap-2"><TeamLogo team={team} size={28} /><span className="truncate">{placeholder}</span></div>;
};

export default function SchedulePage() {
    const { t, lang } = useT();
    const [games, setGames] = useState([]);
    const [teams, setTeams] = useState([]);
    const [iceFilter, setIceFilter] = useState("all");
    const [catFilter, setCatFilter] = useState("all");

    const loadGames = () => api.get("/games").then((r) => setGames(r.data));
    useEffect(() => {
        // Teams rarely change: fetch ONCE on mount (avoids re-downloading hundreds of
        // base64 logos every 15s which was the main cause of slowness).
        api.get("/teams?light=true").then((r) => setTeams(r.data));
        loadGames();
        const i = setInterval(loadGames, 15000);
        return () => clearInterval(i);
    }, []);

    const teamMap = Object.fromEntries(teams.map((team) => [team.id, team]));

    const filtered = games.filter((g) => {
        if (iceFilter !== "all" && g.ice !== iceFilter) return false;
        if (catFilter !== "all" && g.category !== catFilter) return false;
        return true;
    });

    const grouped = useMemo(() => {
        const m = {};
        filtered.forEach((g) => {
            m[g.day] = m[g.day] || [];
            m[g.day].push(g);
        });
        Object.keys(m).forEach((d) => m[d].sort((a, b) => a.time.localeCompare(b.time)));
        return m;
    }, [filtered]);

    return (
        <div data-testid="schedule-page">
            <Helmet><title>{`${t("schedule.title_pre")} ${t("schedule.title_accent")} — ${TOURNAMENT.name}`}</title></Helmet>
            <Section>
                <SectionTitle kicker={t("schedule.kicker")} title={t("schedule.title_pre")} accent={t("schedule.title_accent")} />
                <div className="flex flex-wrap items-center gap-3 mb-8">
                    <div className="flex items-center gap-2 text-white/60 text-sm"><Filter size={16} /> {t("schedule.filters")}</div>
                    <select data-testid="filter-ice" value={iceFilter} onChange={(e) => setIceFilter(e.target.value)} className="input-field max-w-[180px]">
                        <option value="all">{t("schedule.all_surfaces")}</option>
                        {TOURNAMENT.ices.map((i) => (<option key={i} value={i}>{i}</option>))}
                    </select>
                    <select data-testid="filter-category" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="input-field max-w-[160px]">
                        <option value="all">{t("schedule.all_categories")}</option>
                        {TOURNAMENT.categories.map((c) => (<option key={c} value={c}>{c}</option>))}
                    </select>
                </div>

                {Object.keys(grouped).sort().map((day) => (
                    <div key={day} className="mb-12">
                        <div className="flex items-baseline gap-4 mb-4 border-b border-white/10 pb-2">
                            <h3 className="font-display text-3xl uppercase tracking-wider text-[#7FD93C]">{formatDayLocalized(day, lang)}</h3>
                            <span className="text-xs text-white/40 font-mono">{day}</span>
                        </div>

                        <div className="lg:hidden grid gap-3">
                            {grouped[day].map((g) => {
                                const awayTeam = teamMap[g.away_team_id];
                                const homeTeam = teamMap[g.home_team_id];
                                return (
                                <div key={g.game_num} className={`glass-card p-4 block ${g.status === "live" ? "border-l-4 border-[#7FD93C]" : ""}`} data-testid={`schedule-card-${g.game_num}`}>
                                    <Link to={`/match/${g.game_num}`} className="flex justify-between items-center mb-3 hover:text-[#7FD93C]">
                                        <span className="text-xs text-white/60 font-mono">#{g.game_num} • {g.time} • {g.ice}</span>
                                        <StatusBadge status={g.status} />
                                    </Link>
                                    <div className="space-y-2">
                                        <TeamRow team={awayTeam} placeholder={g.away_placeholder} score={g.away_score} />
                                        <TeamRow team={homeTeam} placeholder={g.home_placeholder} score={g.home_score} />
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
                                        <span className="text-white/40">{g.category}</span>
                                        {g.phase && g.phase !== "preliminaire" && (
                                            <span className="px-2 py-0.5 angle-tag bg-[#4FE0D5]/15 text-[#4FE0D5] font-bold" data-testid={`phase-badge-${g.game_num}`}>{phaseLabel(g.phase, lang)}</span>
                                        )}
                                        <Link to={`/live/${g.game_num}`} className="ml-auto px-2 py-0.5 bg-[#7FD93C]/15 text-[#7FD93C] hover:bg-[#7FD93C]/25 font-bold flex items-center gap-1" data-testid={`live-link-${g.game_num}`} onClick={e => e.stopPropagation()}>
                                            <span className="w-1.5 h-1.5 bg-[#7FD93C] rounded-full inline-block animate-pulse" />LIVE
                                        </Link>
                                    </div>
                                </div>
                                );
                            })}
                        </div>

                        <div className="hidden lg:block overflow-x-auto">
                            <table className="tx-table">
                                <thead>
                                    <tr>
                                        <th>{t("schedule.col_game")}</th><th>{t("schedule.col_time")}</th><th>{t("schedule.col_surface")}</th><th>{t("schedule.col_category")}</th><th>{t("schedule.col_teams")}</th><th>{t("schedule.col_score")}</th><th>{t("schedule.col_teams")}</th><th>{t("schedule.col_status")}</th><th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {grouped[day].map((g) => (
                                        <motion.tr key={g.game_num} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={g.status === "live" ? "bg-[#7FD93C]/5" : ""} data-testid={`schedule-row-${g.game_num}`}>
                                            <td><Link to={`/match/${g.game_num}`} className="text-[#4FE0D5] hover:text-[#7FD93C] font-bold">#{g.game_num}</Link></td>
                                            <td>{g.time}</td>
                                            <td className="text-white/70">{g.ice}</td>
                                            <td>
                                                <span className="px-2 py-1 angle-tag bg-white/5 text-[10px] tracking-widest font-bold">{g.category}</span>
                                                {g.phase && g.phase !== "preliminaire" && (
                                                    <span className="ml-1 px-2 py-1 angle-tag bg-[#4FE0D5]/15 text-[#4FE0D5] text-[10px] tracking-widest font-bold" data-testid={`phase-badge-${g.game_num}`}>{phaseLabel(g.phase, lang)}</span>
                                                )}
                                            </td>
                                            <td><TeamCell team={teamMap[g.away_team_id]} placeholder={g.away_placeholder} /></td>
                                            <td className="font-display text-xl">{g.away_score}<span className="text-white/30 mx-1">—</span>{g.home_score}</td>
                                            <td><TeamCell team={teamMap[g.home_team_id]} placeholder={g.home_placeholder} /></td>
                                            <td><StatusBadge status={g.status} /></td>
                                            <td>
                                                <Link to={`/live/${g.game_num}`} className="px-2 py-1 bg-[#7FD93C]/15 text-[#7FD93C] hover:bg-[#7FD93C]/25 text-[10px] font-bold flex items-center gap-1 w-fit" data-testid={`live-link-row-${g.game_num}`}>
                                                    <span className="w-1.5 h-1.5 bg-[#7FD93C] rounded-full inline-block animate-pulse" />LIVE
                                                </Link>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
                {Object.keys(grouped).length === 0 && (
                    <Reveal><div className="text-center text-white/50 py-20">{t("schedule.no_games")}</div></Reveal>
                )}
            </Section>
        </div>
    );
}
